import { useState, useEffect, useCallback, useMemo } from 'react';
import { getDncBounceList, markDncBounce, unmarkLead, getDncDomains, removeDncDomain, downloadDncBounce } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './DncBounce.css';

function parseEmails(raw) {
  return [...new Set(
    raw.split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
  )];
}

function parseDomains(raw) {
  return [...new Set(
    raw.split(/[\n,;]+/)
      .map(d => d.trim().toLowerCase().replace(/^@/, ''))
      .filter(d => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d))
  )];
}

export default function DncBounce({ initialTab }) {
  const { auth } = useAuth();
  const isAdmin = auth?.role === 'ROLE_ADMIN';

  const [activeTab, setActiveTab] = useState(initialTab || 'DNC');

  useEffect(() => {
    if (initialTab && (initialTab === 'DNC' || initialTab === 'BOUNCE')) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const [leads, setLeads] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dncCount, setDncCount] = useState(0);
  const [bounceCount, setBounceCount] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [dncDomains, setDncDomains] = useState([]);
  const [showDomains, setShowDomains] = useState(false);
  const [removingDomain, setRemovingDomain] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [addMode, setAddMode] = useState('email');
  const [emailInput, setEmailInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [reason, setReason] = useState('');
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);

  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchLeads = useCallback(async (tab, srch, pg) => {
    setLoading(true);
    setError('');
    try {
      const res = await getDncBounceList({
        status: tab,
        search: srch || undefined,
        page: pg,
        size: 20,
        sort: 'updatedAt,desc',
      });
      setLeads(res.data.content ?? []);
      setTotalPages(res.data.totalPages ?? 0);
      setTotalElements(res.data.totalElements ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load list');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const [d, b] = await Promise.all([
        getDncBounceList({ status: 'DNC', page: 0, size: 1 }),
        getDncBounceList({ status: 'BOUNCE', page: 0, size: 1 }),
      ]);
      setDncCount(d.data.totalElements ?? 0);
      setBounceCount(b.data.totalElements ?? 0);
    } catch { /* decorative */ }
  }, []);

  const fetchDomains = useCallback(async () => {
    try {
      const res = await getDncDomains();
      setDncDomains(res.data ?? []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLeads(activeTab, debouncedSearch, page); }, [activeTab, debouncedSearch, page, fetchLeads]);
  useEffect(() => { fetchCounts(); fetchDomains(); }, [fetchCounts, fetchDomains]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch('');
    setDebouncedSearch('');
    setPage(0);
  };

  const parsedEmails = useMemo(() => parseEmails(emailInput), [emailInput]);
  const parsedDomains = useMemo(() => parseDomains(domainInput), [domainInput]);
  const isDomainMode = activeTab === 'DNC' && addMode === 'domain';
  const previewValues = isDomainMode ? parsedDomains : parsedEmails;

  const openModal = () => {
    setEmailInput(''); setDomainInput(''); setReason(''); setModalError(''); setAddMode('email'); setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false); setEmailInput(''); setDomainInput(''); setReason(''); setModalError('');
  };

  const handleSave = async () => {
    if (previewValues.length === 0) {
      setModalError(isDomainMode ? 'No valid domains to add.' : 'No valid email addresses to add.');
      return;
    }
    if (!reason.trim()) {
      setModalError('Please provide a reason.');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      const payload = { status: activeTab, entryType: isDomainMode ? 'DOMAIN' : 'EMAIL', values: previewValues };
      if (reason.trim()) payload.reason = reason.trim();
      await markDncBounce(payload);
      closeModal();
      await Promise.all([fetchLeads(activeTab, debouncedSearch, page), fetchCounts(), fetchDomains()]);
    } catch (err) {
      setModalError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUnmark = async (leadId) => {
    setRemoving(leadId);
    try {
      await unmarkLead(leadId);
      setRemoveConfirm(null);
      await Promise.all([fetchLeads(activeTab, debouncedSearch, page), fetchCounts()]);
    } catch (err) {
      setError(err.message || 'Failed to remove lead');
    } finally {
      setRemoving(null);
    }
  };

  const handleRemoveDomain = async (id) => {
    setRemovingDomain(id);
    try {
      await removeDncDomain(id);
      await fetchDomains();
    } catch (err) {
      setError(err.message || 'Failed to remove domain');
    } finally {
      setRemovingDomain(null);
    }
  };

  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await downloadDncBounce(activeTab);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab.toLowerCase()}-list.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const tabLabel = activeTab === 'DNC' ? 'DNC' : 'Bounce';

  return (
    <div className="db-root">

      <div className="db-stats-row">
        <div className={`db-stat-card ${activeTab === 'DNC' ? 'db-stat-card--active' : ''}`} onClick={() => handleTabChange('DNC')}>
          <div className="db-stat-icon db-stat-icon--dnc">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
          </div>
          <div className="db-stat-body">
            <span className="db-stat-value">{dncCount.toLocaleString()}</span>
            <span className="db-stat-label">Do Not Contact</span>
          </div>
        </div>
        <div className={`db-stat-card ${activeTab === 'BOUNCE' ? 'db-stat-card--active' : ''}`} onClick={() => handleTabChange('BOUNCE')}>
          <div className="db-stat-icon db-stat-icon--bounce">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-4.5" /></svg>
          </div>
          <div className="db-stat-body">
            <span className="db-stat-value">{bounceCount.toLocaleString()}</span>
            <span className="db-stat-label">Bounced</span>
          </div>
        </div>
      </div>

      {activeTab === 'DNC' && dncDomains.length > 0 && (
        <div className="db-domains-bar">
          <button className="db-domains-toggle" onClick={() => setShowDomains(v => !v)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            {dncDomains.length} blocked domain{dncDomains.length !== 1 ? 's' : ''}
            <span className="db-domains-caret">{showDomains ? '▲' : '▼'}</span>
          </button>
          {showDomains && (
            <div className="db-domains-list">
              {dncDomains.map(d => (
                <span key={d.id} className="db-domain-chip">
                  @{d.domain}
                  <button
                    className="db-domain-remove"
                    onClick={() => handleRemoveDomain(d.id)}
                    disabled={removingDomain === d.id}
                    title="Remove domain rule"
                  >
                    {removingDomain === d.id ? '…' : '✕'}
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="db-toolbar">
        <div className="db-toggle-wrap">
          <button className={`db-toggle-btn ${activeTab === 'DNC' ? 'active' : ''}`} onClick={() => handleTabChange('DNC')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
            DNC
          </button>
          <button className={`db-toggle-btn ${activeTab === 'BOUNCE' ? 'active' : ''}`} onClick={() => handleTabChange('BOUNCE')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-4.5" /></svg>
            Bounce
          </button>
        </div>

        <div className="db-search-wrap">
          <svg className="db-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="db-search"
            placeholder={`Search ${tabLabel} leads…`}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="db-search-clear" onClick={() => { setSearch(''); setPage(0); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {isAdmin && (
          <button className="db-download-btn" onClick={handleDownload} disabled={downloading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {downloading ? 'Downloading…' : `Export ${tabLabel}`}
          </button>
        )}

        <button className={`db-add-btn ${activeTab === 'DNC' ? 'db-add-btn--dnc' : 'db-add-btn--bounce'}`} onClick={openModal}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
          </svg>
          Add to {tabLabel}
        </button>
      </div>

      {totalElements > 0 && (
        <p className="db-summary">
          {debouncedSearch
            ? <>Showing <strong>{leads.length}</strong> of <strong>{totalElements}</strong> for "<strong>{debouncedSearch}</strong>"</>
            : <><strong>{totalElements.toLocaleString()}</strong> lead{totalElements !== 1 ? 's' : ''} in the {tabLabel} list</>
          }
        </p>
      )}

      {error && <div className="db-error-banner">{error}</div>}

      <div className="db-table-wrap">
        {loading ? (
          <div className="db-empty"><div className="db-spinner" /><span>Loading…</span></div>
        ) : leads.length === 0 ? (
          <div className="db-empty">
            {debouncedSearch ? (
              <>
                <div className="db-empty-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></div>
                <span>No results for "{debouncedSearch}"</span>
                <small>Try a different search term</small>
              </>
            ) : (
              <>
                <div className={`db-empty-icon db-empty-icon--${activeTab.toLowerCase()}`}>
                  {activeTab === 'DNC'
                    ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                    : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-4.5" /></svg>
                  }
                </div>
                <span>No {tabLabel} leads yet</span>
                <small>Click "Add to {tabLabel}" to mark leads{activeTab === 'DNC' ? ' or entire domains' : ''}</small>
                <button className={`db-empty-cta ${activeTab === 'DNC' ? 'db-add-btn--dnc' : 'db-add-btn--bounce'}`} onClick={openModal}>Add to {tabLabel}</button>
              </>
            )}
          </div>
        ) : (
          <table className="db-table">
            <thead>
              <tr>
                <th className="db-th db-th-sm">#</th>
                <th className="db-th">Name</th>
                <th className="db-th">Email</th>
                <th className="db-th">Reason</th>
                <th className="db-th">Company</th>
                <th className="db-th">Status</th>
                <th className="db-th">Updated By</th>
                <th className="db-th">Updated At</th>
                <th className="db-th db-th-action">Remove</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, idx) => (
                <tr key={lead.id} className="db-tr">
                  <td className="db-td db-td-muted"><span className="db-row-num">{page * 20 + idx + 1}</span></td>
                  <td className="db-td">
                    <div className="db-name-cell">
                      <div className="db-avatar">{(lead.firstName?.[0] ?? '') + (lead.lastName?.[0] ?? '')}</div>
                      <div>
                        <div className="db-name">{lead.firstName} {lead.lastName}</div>
                        <div className="db-sub">{lead.designation}</div>
                      </div>
                    </div>
                  </td>
                  <td className="db-td">
                    <div className="db-email-cell">
                      <div className={`db-email-dot db-email-dot--${activeTab.toLowerCase()}`} />
                      <span className="db-email">{lead.email}</span>
                    </div>
                  </td>
                  <td className="db-td db-td-muted">{lead.reason || '—'}</td>
                  <td className="db-td db-td-muted">{lead.companyName}</td>
                  <td className="db-td">
                    <span className={`db-status-badge db-status-badge--${activeTab.toLowerCase()}`}>{activeTab}</span>
                  </td>
                  <td className="db-td db-td-muted">{lead.updatedBy || lead.createdBy || '—'}</td>
                  <td className="db-td db-td-muted">{formatDate(lead.updatedAt || lead.createdAt)}</td>
                  <td className="db-td db-td-action">
                    {removeConfirm === lead.id ? (
                      <div className="db-confirm-inline">
                        <button className="db-confirm-yes" onClick={() => handleUnmark(lead.id)} disabled={removing === lead.id}>
                          {removing === lead.id ? '…' : 'Yes'}
                        </button>
                        <button className="db-confirm-no" onClick={() => setRemoveConfirm(null)}>No</button>
                      </div>
                    ) : (
                      <button className="db-remove-btn" onClick={() => setRemoveConfirm(lead.id)} title="Set Active">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="db-pagination">
          <button className="db-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="db-page-info">Page {page + 1} of {totalPages}</span>
          <button className="db-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {modalOpen && (
        <div className="db-modal-backdrop" onClick={closeModal}>
          <div className="db-modal" onClick={e => e.stopPropagation()}>
            <div className="db-modal-header">
              <div className="db-modal-title-wrap">
                <span className={`db-modal-pill db-modal-pill--${activeTab.toLowerCase()}`}>{tabLabel}</span>
                <h2 className="db-modal-title">Add to {tabLabel} List</h2>
              </div>
              <button className="db-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="db-modal-body">
              {activeTab === 'DNC' && (
                <div className="db-mode-tabs">
                  <button className={`db-mode-tab ${addMode === 'email' ? 'db-mode-tab--active' : ''}`} onClick={() => { setAddMode('email'); setModalError(''); }}>
                    Email Addresses
                  </button>
                  <button className={`db-mode-tab ${addMode === 'domain' ? 'db-mode-tab--active' : ''}`} onClick={() => { setAddMode('domain'); setModalError(''); }}>
                    Domain
                  </button>
                </div>
              )}

              {isDomainMode ? (
                <>
                  <div className="db-modal-hint">
                    Paste domain names (e.g. <code>acme.com</code>). All existing and future leads from those domains will be marked DNC.
                  </div>
                  <div className="db-modal-field">
                    <label className="db-modal-label">Domains</label>
                    <textarea className="db-modal-textarea" placeholder={`acme.com\nexample.org`} value={domainInput}
                      onChange={e => { setDomainInput(e.target.value); setModalError(''); }} rows={6} autoFocus />
                  </div>
                  {parsedDomains.length > 0 && (
                    <div className="db-preview">
                      <div className="db-preview-header">
                        <span className="db-preview-title">Preview</span>
                        <span className="db-preview-new">{parsedDomains.length} domain{parsedDomains.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="db-preview-list">
                        {parsedDomains.slice(0, 10).map(d => <span key={d} className="db-preview-chip">@{d}</span>)}
                        {parsedDomains.length > 10 && <span className="db-preview-more">+{parsedDomains.length - 10} more</span>}
                      </div>
                    </div>
                  )}
                  <div className="db-modal-field" style={{ marginTop: 14 }}>
                    <label className="db-modal-label">Reason <span className="db-required">*</span></label>
                    <textarea className="db-modal-textarea" placeholder="Why are these domains being added to DNC?" value={reason}
                      onChange={e => { setReason(e.target.value); setModalError(''); }} rows={3} maxLength={50} />
                    <span className="db-char-count">{reason.length}/50</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="db-modal-hint">
                    Paste email addresses separated by commas, semicolons, or new lines.
                  </div>
                  <div className="db-modal-field">
                    <label className="db-modal-label">Email Addresses</label>
                    <textarea className="db-modal-textarea" placeholder={`john@example.com\njane@company.org`} value={emailInput}
                      onChange={e => { setEmailInput(e.target.value); setModalError(''); }} rows={7} autoFocus />
                  </div>
                  {parsedEmails.length > 0 && (
                    <div className="db-preview">
                      <div className="db-preview-header">
                        <span className="db-preview-title">Preview</span>
                        <span className="db-preview-new">{parsedEmails.length} email{parsedEmails.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="db-preview-list">
                        {parsedEmails.slice(0, 10).map(e => <span key={e} className="db-preview-chip">{e}</span>)}
                        {parsedEmails.length > 10 && <span className="db-preview-more">+{parsedEmails.length - 10} more</span>}
                      </div>
                    </div>
                  )}
                  <div className="db-modal-field" style={{ marginTop: 14 }}>
                    <label className="db-modal-label">Reason <span className="db-required">*</span></label>
                    <textarea className="db-modal-textarea" placeholder={`Enter reason for adding to ${tabLabel} list`} value={reason}
                      onChange={e => { setReason(e.target.value); setModalError(''); }} rows={3} maxLength={50} />
                    <span className="db-char-count">{reason.length}/50</span>
                  </div>
                </>
              )}
              {modalError && <div className="db-modal-error">{modalError}</div>}
            </div>

            <div className="db-modal-footer">
              <div className="db-modal-footer-left">
                {previewValues.length > 0 && (
                  <span className="db-modal-count">
                    <strong>{previewValues.length}</strong> {isDomainMode ? 'domain' : 'email'}{previewValues.length !== 1 ? 's' : ''} will be added
                  </span>
                )}
              </div>
              <div className="db-modal-footer-right">
                <button className="db-btn db-btn-ghost" onClick={closeModal} disabled={saving}>Cancel</button>
                <button className={`db-btn ${activeTab === 'DNC' ? 'db-btn-dnc' : 'db-btn-bounce'}`} onClick={handleSave} disabled={previewValues.length === 0 || !reason.trim() || saving}>
                  {saving ? 'Saving…' : `Save to ${tabLabel}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
